/**
 * MetaApi enum values, transcribed from metaapi.cloud-sdk@29.3.3 typings
 * (`clients/metaApi/metaApiWebsocket.client.schemas.d.ts`). The SDK types these fields as
 * bare `string`, so these constants are what give us exhaustiveness on our side.
 */

/** How a deal relates to a position: opening it, closing it, or reversing. */
export const DEAL_ENTRY = {
  in: "DEAL_ENTRY_IN",
  out: "DEAL_ENTRY_OUT",
  inOut: "DEAL_ENTRY_INOUT",
  outBy: "DEAL_ENTRY_OUT_BY",
} as const;

/**
 * Why the deal happened. Note the SDK declares `reason` as OPTIONAL — MetaApi synthesizes
 * deals for MT4, which has no native deal concept, so this may be absent or
 * DEAL_REASON_UNKNOWN on an MT4 account. Never branch on it without a fallback;
 * see the classification chain in the trade engine.
 */
export const DEAL_REASON = {
  client: "DEAL_REASON_CLIENT",
  mobile: "DEAL_REASON_MOBILE",
  web: "DEAL_REASON_WEB",
  expert: "DEAL_REASON_EXPERT",
  stopLoss: "DEAL_REASON_SL",
  takeProfit: "DEAL_REASON_TP",
  stopOut: "DEAL_REASON_SO",
  rollover: "DEAL_REASON_ROLLOVER",
  variationMargin: "DEAL_REASON_VMARGIN",
  split: "DEAL_REASON_SPLIT",
  unknown: "DEAL_REASON_UNKNOWN",
} as const;

export type DealReason = (typeof DEAL_REASON)[keyof typeof DEAL_REASON];

/**
 * Reasons that mean a human closed the trade themselves — we stop managing it.
 *
 * Measured 2026-08-10 (`docs/spike-findings.md` §2): on this broker, closing a position via
 * the MetaApi trade API — which is exactly what our own `closeOrder()` does — reports
 * `DEAL_REASON_UNKNOWN`, not `DEAL_REASON_CLIENT`. So this list will never fire on our own
 * app-initiated closes (correct — those already carry `close_reason` from the caller).
 *
 * **Confirmed dead for every closure type measured, including mobile.** A real hand-close
 * from the MT4 *mobile app* (not a script, not the desktop app) was measured 2026-08-10
 * 09:41 UTC — `deal_id 366601204`, opened and closed in ~7s — and it ALSO reported
 * `DEAL_REASON_UNKNOWN`, not `DEAL_REASON_MOBILE`. This list is now confirmed to never fire
 * on this broker under any closure path tested (app API close, genuine broker-triggered
 * SL/TP, and a hand-close from the phone). **Do not build manual-intervention detection on
 * `deal.reason` — it is not a fallback-of-last-resort here, it is simply unusable.** This is
 * a real, currently-unbuilt gap: `userIntervened` (`ruleContext.ts`) only checks
 * `order.closeReason === "manual"`, which covers an app-button close (that path sets
 * `close_reason` explicitly, independent of the broker's `reason` field) but has no way to
 * detect a close made directly in MT4 by hand — that position would keep being "managed" by
 * the rule engine as if nothing happened. Needs a different signal before going live:
 * candidates are `brokerComment` absence/shape (this deal had none, since it wasn't opened
 * through our clientId scheme, but that only helps distinguish *our* positions from
 * always-external ones) or a terminalState⟂DB divergence check in the reconciler. Not
 * designed yet — flagged rather than silently left unhandled.
 */
export const MANUAL_CLOSE_REASONS: readonly string[] = [
  DEAL_REASON.client,
  DEAL_REASON.mobile,
  DEAL_REASON.web,
];

/** How we decided a position closed at its target or its stop. Recorded per order so the
 *  data eventually shows whether this broker's `reason` field can be trusted at all. */
export const CLOSE_REASON_SOURCE = {
  /** deal.reason said so outright. */
  dealReason: "deal_reason",
  /** The broker appended [tp] / [sl] to the comment — common on MT4. */
  comment: "comment",
  /** Neither was available; the fill price matched one level far better than the other. */
  priceMatch: "price_match",
  /** Derived from surrounding state rather than the deal itself. */
  inferred: "inferred",
} as const;

export type CloseReasonSource = (typeof CLOSE_REASON_SOURCE)[keyof typeof CLOSE_REASON_SOURCE];

/** MT4 supports only these two; DAY and SPECIFIED_DAY are MT5-only. */
export const MT4_ORDER_EXPIRATION = ["ORDER_TIME_GTC", "ORDER_TIME_SPECIFIED"] as const;

/** Matches the `[sl]` / `[tp]` suffix MT4 brokers typically append when a level triggers. */
export const BROKER_COMMENT_CLOSE_RE = /\[(sl|tp)\]/i;
